import { useState } from 'react'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useFlags } from '@equipmentshare/feature-flags'
import PermissionsCheck from 'components/PermissionsCheck'
import { Permission } from '../../../../graphql'

export type ItemActionsMenuProps = {
	onEdit: () => void,
	onAdjustQuantity: () => void,
	onConvertItems: () => void,
	onConvertItemsToMany: () => void,
	onOverrideWac: () => void,
	onDeleteItem: () => void,
}

/**
 * Pure-MUI replacement for the EUI `EditMenu` — same actions, feature-flag gating, and
 * permission checks, rendered with MUI `Menu`/`MenuItem`. New views in this effort use
 * no EUI.
 */
export default function ItemActionsMenu( props: ItemActionsMenuProps ) {
	const { onEdit, onAdjustQuantity, onConvertItems, onConvertItemsToMany, onOverrideWac, onDeleteItem } = props
	const flags = useFlags()
	const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>( null )
	const open = Boolean( anchorEl )

	const conversionFunction = flags.enableTswConvertItemOneToMany ? onConvertItemsToMany : onConvertItems

	const runAction = ( action: () => void ) => () => {
		setAnchorEl( null )
		action()
	}

	return (
		<>
			<Button
				variant="contained"
				endIcon={<ArrowDropDownIcon />}
				onClick={( event ) => setAnchorEl( event.currentTarget )}
			>
				Edit
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl( null )}>
				<MenuItem onClick={runAction( onEdit )}>Edit details</MenuItem>

				<PermissionsCheck
					permissionsToCheck={[ Permission.CanCreateDecrementAdjustmentByStoreId, Permission.CanCreateIncrementAdjustmentByStoreId ]}
					operator="any"
				>
					<MenuItem onClick={runAction( onAdjustQuantity )}>Edit quantity</MenuItem>
				</PermissionsCheck>

				{flags.enableTswWeightedAverageCost && (
					<PermissionsCheck permissionsToCheck={[ Permission.CanOverrideWeightedAverageCostByStoreId ]}>
						<MenuItem onClick={runAction( onOverrideWac )}>Edit WAC</MenuItem>
					</PermissionsCheck>
				)}

				<PermissionsCheck permissionsToCheck={[ Permission.CanConvertPartsByStoreId ]}>
					<MenuItem onClick={runAction( conversionFunction )}>Convert items</MenuItem>
				</PermissionsCheck>

				<MenuItem onClick={runAction( onDeleteItem )}>Delete item</MenuItem>
			</Menu>
		</>
	)
}
